import { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Flame, Circle, Plus, Check } from "lucide-react";
import { Input } from "@/components/ui/input";

const PRIORITY_STYLE = {
  urgent: "text-red-400 border-red-500/40 bg-red-500/10",
  important: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10",
  medium: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
  low: "text-zinc-400 border-white/15 bg-white/5",
};
const PRIORITIES = ["low", "medium", "important", "urgent"];
const FREQS = ["daily", "weekly", "once"];
const FREQ_LABEL = { daily: "daily", weekly: "weekly", once: "one off" };
const FREQ_COLOR = { daily: "text-[#D4AF37]", weekly: "text-[#00E5FF]", once: "text-emerald-400" };
const nextFreq = (f) => FREQS[(FREQS.indexOf(f) + 1) % FREQS.length];

const SHORT_NAME = {
  "party-town": "Party Town",
  "luna-balloon-club": "Luna",
};

export const COLUMNS = [
  { id: "pt-luna", name: "PartyTown × Luna", slugs: ["party-town", "luna-balloon-club"] },
  { id: "offtheplan", name: "OFFTHEPLAN.COM.AU", slugs: ["offtheplan"] },
  { id: "executive-properties", name: "Executive Properties", slugs: ["executive-properties"] },
  { id: "perth-auto-salon", name: "Perth Auto Salon", slugs: ["perth-auto-salon"] },
  { id: "motu", name: "MOTU", slugs: ["motu"] },
  { id: "family-home", name: "Family & Home", slugs: ["family-home"] },
];

const ColumnAdd = ({ column, onAdded }) => {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [freq, setFreq] = useState("daily");
  const [slugIdx, setSlugIdx] = useState(0);
  const slug = column.slugs[slugIdx];

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post(`/entities/${slug}/tasks`, { title, frequency: freq, priority });
      setTitle("");
      toast.success("Focus task added");
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2 mb-4" data-testid={`focus-col-form-${column.id}`}>
      <div className="flex gap-2">
        <Input
          value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task…"
          className="bg-white/5 border-white/10 text-white rounded-sm h-8 text-xs focus-visible:ring-[#D4AF37]"
          data-testid={`focus-col-input-${column.id}`}
        />
        <button
          type="submit"
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 bg-[#D4AF37] text-black rounded-sm hover:bg-[#e5c555] transition-colors duration-200"
          data-testid={`focus-col-add-${column.id}`}
          aria-label="Add task"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPriority(PRIORITIES[(PRIORITIES.indexOf(priority) + 1) % PRIORITIES.length])}
          className={`text-[9px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm border transition-colors duration-200 ${PRIORITY_STYLE[priority]}`}
          data-testid={`focus-col-priority-${column.id}`}
        >
          {priority}
        </button>
        <button
          type="button"
          onClick={() => setFreq(nextFreq(freq))}
          className="text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-white/10 rounded-sm text-zinc-400 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors duration-200"
          data-testid={`focus-col-freq-${column.id}`}
        >
          {FREQ_LABEL[freq]}
        </button>
        {column.slugs.length > 1 && (
          <button
            type="button"
            onClick={() => setSlugIdx((slugIdx + 1) % column.slugs.length)}
            className="text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-white/10 rounded-sm text-zinc-400 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] transition-colors duration-200 ml-auto"
            data-testid={`focus-col-venture-${column.id}`}
          >
            {SHORT_NAME[slug] || slug}
          </button>
        )}
      </div>
    </form>
  );
};

export const FocusBoard = ({ tasks, onToggle, onAdded }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ title: "", priority: "medium", frequency: "daily" });

  const startEdit = (t) => {
    setEditingId(t.id);
    setDraft({ title: t.title, priority: t.priority || "medium", frequency: t.frequency });
  };

  const saveEdit = async (id) => {
    if (!draft.title.trim()) { toast.error("Title cannot be empty"); return; }
    try {
      await api.patch(`/tasks/${id}`, draft);
      setEditingId(null);
      toast.success("Task updated");
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="focus-section">
      <div className="border border-white/10 rounded-md bg-[#0F0F11] p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <Flame className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-display text-2xl text-white" data-testid="focus-heading">Focus Tasks</h3>
          <span className="text-xs text-zinc-500 uppercase tracking-widest ml-auto" data-testid="focus-count">{tasks.length} open</span>
        </div>
        <p className="text-xs text-zinc-500 mb-6">One column per venture — sorted by priority. Small wins, big empires. Scroll sideways for the full board.</p>

        <div className="flex gap-4 overflow-x-auto pb-4 snap-x scroll-smooth" data-testid="focus-columns">
          {COLUMNS.map((col) => {
            const colTasks = tasks.filter((t) => col.slugs.includes(t.slug));
            return (
              <div
                key={col.id}
                className="w-[78vw] min-w-[78vw] sm:w-[262px] sm:min-w-[262px] flex-shrink-0 snap-start border border-white/10 rounded-sm bg-black/30 p-4 flex flex-col"
                data-testid={`focus-column-${col.id}`}
              >
                <div className="flex items-baseline justify-between mb-4">
                  <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white leading-snug" data-testid={`focus-column-title-${col.id}`}>
                    {col.name}
                  </h4>
                  <span className="text-[10px] text-zinc-500 shrink-0 ml-2" data-testid={`focus-column-count-${col.id}`}>{colTasks.length}</span>
                </div>

                <ColumnAdd column={col} onAdded={onAdded} />

                {colTasks.length === 0 ? (
                  <p className="text-xs text-zinc-600 italic" data-testid={`focus-column-empty-${col.id}`}>All clear.</p>
                ) : (
                  <div className="space-y-3">
                    {colTasks.map((t, i) => {
                      const prio = t.priority || "medium";
                      const isEditing = editingId === t.id;
                      return (
                        <motion.div
                          key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
                          className={`border rounded-sm bg-black/40 p-3 flex flex-col gap-2 transition-colors duration-200 ${isEditing ? "border-[#D4AF37]/50" : "border-white/10 hover:border-white/25"}`}
                          data-testid={`focus-task-${t.id}`}
                        >
                          {isEditing ? (
                            <>
                              <Input
                                value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} autoFocus
                                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t.id); }}
                                className="bg-white/5 border-white/10 text-white rounded-sm h-8 text-xs focus-visible:ring-[#D4AF37]"
                                data-testid={`focus-task-edit-input-${t.id}`}
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setDraft((d) => ({ ...d, priority: PRIORITIES[(PRIORITIES.indexOf(d.priority) + 1) % PRIORITIES.length] }))}
                                  className={`text-[9px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm border transition-colors duration-200 ${PRIORITY_STYLE[draft.priority]}`}
                                  data-testid={`focus-task-edit-priority-${t.id}`}
                                >
                                  {draft.priority}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDraft((d) => ({ ...d, frequency: nextFreq(d.frequency) }))}
                                  className="text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-white/10 rounded-sm text-zinc-400 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors duration-200"
                                  data-testid={`focus-task-edit-freq-${t.id}`}
                                >
                                  {FREQ_LABEL[draft.frequency]}
                                </button>
                                <button
                                  type="button" onClick={() => saveEdit(t.id)}
                                  className="ml-auto inline-flex items-center justify-center w-7 h-7 bg-[#D4AF37] text-black rounded-sm hover:bg-[#e5c555] transition-colors duration-200"
                                  data-testid={`focus-task-edit-save-${t.id}`} aria-label="Save task"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-start justify-between gap-2">
                                <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border ${PRIORITY_STYLE[prio]}`} data-testid={`focus-task-priority-${t.id}`}>
                                  {prio}
                                </span>
                                <button
                                  onClick={() => onToggle(t.id)}
                                  className="p-2 -m-2 text-zinc-500 hover:text-[#D4AF37] transition-colors duration-200"
                                  data-testid={`focus-task-toggle-${t.id}`}
                                >
                                  <Circle className="w-5 h-5" />
                                </button>
                              </div>
                              <p
                                className="text-sm text-zinc-200 leading-snug cursor-pointer hover:text-white transition-colors duration-200"
                                onClick={() => startEdit(t)}
                                title="Tap to edit"
                                data-testid={`focus-task-title-${t.id}`}
                              >
                                {t.title}
                              </p>
                              <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-zinc-500">
                                <span className="truncate">{col.slugs.length > 1 ? (SHORT_NAME[t.slug] || t.slug) : ""}</span>
                                <span className={FREQ_COLOR[t.frequency] || "text-zinc-400"}>{FREQ_LABEL[t.frequency] || t.frequency}</span>
                              </div>
                            </>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
